import React from "react";
import { Eyebrow } from "@/components/Section";

/**
 * SVG-based visual explainers for the Schwachstellenmanagement page.
 * Each illustration is designed to make an abstract concept immediately understandable.
 * Uses the site palette: navy primary, cyan, lime accent, ink.
 */

const NAVY = "#1A4D6E";
const CYAN = "#1DA9DA";
const LIME = "#A4C539";
const INK = "#101923";
const LIGHT = "#F5F7F8";
const RED = "#E11D48";

// 1. INVENTARISIERUNG – "Wir zählen zuerst, was Sie besitzen"
const InventoryIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="Inventarisierung der IT-Landschaft">
    <rect width="400" height="260" fill={LIGHT} />
    {/* Grid of devices */}
    {[0, 1, 2].map((row) =>
      [0, 1, 2, 3].map((col) => {
        const x = 45 + col * 80;
        const y = 40 + row * 60;
        const scanned = (row + col) % 2 === 0;
        return (
          <g key={`${row}-${col}`}>
            <rect x={x} y={y} width={60} height={44} rx={4} fill={scanned ? NAVY : "#D3DEE6"} />
            <rect x={x + 22} y={y + 46} width={16} height={4} fill={scanned ? NAVY : "#D3DEE6"} />
            <rect x={x + 12} y={y + 50} width={36} height={3} fill={scanned ? NAVY : "#D3DEE6"} />
            {scanned && (
              <circle cx={x + 52} cy={y + 8} r={5} fill={LIME}>
                <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
              </circle>
            )}
          </g>
        );
      })
    )}
    {/* Scanning line */}
    <line x1="20" y1="130" x2="380" y2="130" stroke={LIME} strokeWidth="2" opacity="0.7">
      <animate attributeName="y1" values="30;220;30" dur="4s" repeatCount="indefinite" />
      <animate attributeName="y2" values="30;220;30" dur="4s" repeatCount="indefinite" />
    </line>
    <text x="200" y="245" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill={INK} letterSpacing="2">
      12 GERÄTE · 3 SERVER · 1 FIREWALL
    </text>
  </svg>
);

// 2. SCAN – "Wir suchen nach Lücken"
const ScanIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="Schwachstellen-Scan mit Radar">
    <rect width="400" height="260" fill={INK} />
    {/* Radar sweep */}
    <circle cx="200" cy="130" r="100" fill="none" stroke={CYAN} strokeWidth="1" opacity="0.3" />
    <circle cx="200" cy="130" r="70" fill="none" stroke={CYAN} strokeWidth="1" opacity="0.3" />
    <circle cx="200" cy="130" r="40" fill="none" stroke={CYAN} strokeWidth="1" opacity="0.3" />
    {/* Sweeping wedge */}
    <g style={{ transformOrigin: "200px 130px" }}>
      <path d="M 200 130 L 200 30 A 100 100 0 0 1 285 80 Z" fill={LIME} opacity="0.35">
        <animateTransform attributeName="transform" type="rotate" from="0 200 130" to="360 200 130" dur="4s" repeatCount="indefinite" />
      </path>
    </g>
    {/* Blips (vulnerabilities detected) */}
    <circle cx="150" cy="90" r="4" fill={RED}>
      <animate attributeName="opacity" values="0;1;0" dur="4s" repeatCount="indefinite" begin="0.5s" />
    </circle>
    <circle cx="240" cy="160" r="4" fill={RED}>
      <animate attributeName="opacity" values="0;1;0" dur="4s" repeatCount="indefinite" begin="1.5s" />
    </circle>
    <circle cx="180" cy="180" r="4" fill="#F59E0B">
      <animate attributeName="opacity" values="0;1;0" dur="4s" repeatCount="indefinite" begin="2.5s" />
    </circle>
    <circle cx="230" cy="100" r="4" fill="#F59E0B">
      <animate attributeName="opacity" values="0;1;0" dur="4s" repeatCount="indefinite" begin="3.5s" />
    </circle>
    {/* Center dot */}
    <circle cx="200" cy="130" r="6" fill={LIME} />
    <text x="200" y="245" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill="#fff" letterSpacing="2">
      SCAN LÄUFT · 4 AUFFÄLLIGKEITEN
    </text>
  </svg>
);

// 3. CVSS-Bewertung – "Wie schlimm ist es wirklich?"
const CvssIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="CVSS Risikobewertung">
    <rect width="400" height="260" fill={LIGHT} />
    {/* Gauge background */}
    <path d="M 60 200 A 140 140 0 0 1 340 200" stroke="#E5EAF0" strokeWidth="24" fill="none" strokeLinecap="round" />
    {/* Gauge fill (0-7 CVSS High) */}
    <path d="M 60 200 A 140 140 0 0 1 340 200" stroke="url(#gaugeGrad)" strokeWidth="24" fill="none" strokeLinecap="round" strokeDasharray="440" strokeDashoffset="132" />
    <defs>
      <linearGradient id="gaugeGrad">
        <stop offset="0%" stopColor={LIME} />
        <stop offset="45%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor={RED} />
      </linearGradient>
    </defs>
    {/* Needle */}
    <line x1="200" y1="200" x2="270" y2="120" stroke={INK} strokeWidth="4" strokeLinecap="round">
      <animate attributeName="x2" values="130;270;130" dur="4s" repeatCount="indefinite" />
      <animate attributeName="y2" values="120;120;120" dur="4s" repeatCount="indefinite" />
    </line>
    <circle cx="200" cy="200" r="10" fill={INK} />
    {/* Labels */}
    <text x="70" y="230" fontFamily="ui-monospace, monospace" fontSize="10" fill={LIME} fontWeight="bold">LOW</text>
    <text x="185" y="90" fontFamily="ui-monospace, monospace" fontSize="10" fill="#F59E0B" fontWeight="bold">MEDIUM</text>
    <text x="305" y="230" fontFamily="ui-monospace, monospace" fontSize="10" fill={RED} fontWeight="bold">CRITICAL</text>
    {/* Score */}
    <text x="200" y="175" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="42" fill={INK} fontWeight="900">7.4</text>
    <text x="200" y="255" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill={INK} letterSpacing="2">
      CVSS-SCORE · HOCH
    </text>
  </svg>
);

// 4. PATCHING – "Wir schließen die Lücken"
const PatchIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="Automatisierte Patch-Zyklen">
    <rect width="400" height="260" fill={INK} />
    {/* Shield */}
    <path d="M 200 40 L 260 60 L 260 130 Q 260 180 200 210 Q 140 180 140 130 L 140 60 Z" fill={NAVY} stroke={LIME} strokeWidth="2" />
    {/* Checkmark */}
    <path d="M 170 130 L 190 150 L 230 105" stroke={LIME} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <animate attributeName="stroke-dasharray" values="0 100;100 0" dur="2s" repeatCount="indefinite" />
    </path>
    {/* Rotating arrows around shield */}
    <g style={{ transformOrigin: "200px 130px" }}>
      <g>
        <path d="M 200 40 A 90 90 0 0 1 285 130" fill="none" stroke={CYAN} strokeWidth="3" strokeLinecap="round" opacity="0.7" />
        <polygon points="285,130 275,120 275,140" fill={CYAN} />
        <animateTransform attributeName="transform" type="rotate" from="0 200 130" to="360 200 130" dur="6s" repeatCount="indefinite" />
      </g>
    </g>
    {/* Patch labels floating */}
    <rect x="20" y="30" width="60" height="18" rx="2" fill={LIME} opacity="0.9" />
    <text x="50" y="43" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK} fontWeight="bold">CVE-2025-1234</text>
    <rect x="320" y="30" width="60" height="18" rx="2" fill={LIME} opacity="0.9" />
    <text x="350" y="43" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK} fontWeight="bold">CVE-2025-9821</text>
    <rect x="20" y="212" width="60" height="18" rx="2" fill={LIME} opacity="0.9" />
    <text x="50" y="225" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK} fontWeight="bold">PATCHED</text>
    <text x="200" y="245" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill="#fff" letterSpacing="2">
      LÜCKEN GESCHLOSSEN
    </text>
  </svg>
);

// 5. MONITORING – "Wir bleiben wachsam"
const MonitorIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="24/7 Monitoring Dashboard">
    <rect width="400" height="260" fill={LIGHT} />
    {/* Dashboard frame */}
    <rect x="40" y="30" width="320" height="200" rx="8" fill="#fff" stroke={NAVY} strokeWidth="2" />
    {/* Header bar */}
    <rect x="40" y="30" width="320" height="24" rx="8" fill={NAVY} />
    <circle cx="55" cy="42" r="3" fill={LIME} />
    <text x="70" y="46" fontFamily="ui-monospace, monospace" fontSize="10" fill="#fff" letterSpacing="1">SOC DASHBOARD · LIVE</text>
    {/* Chart area */}
    <polyline points="60,180 90,160 120,170 150,140 180,150 210,110 240,120 270,90 300,100 340,70" fill="none" stroke={CYAN} strokeWidth="3" />
    <polyline points="60,200 90,190 120,195 150,175 180,180 210,155 240,160 270,145 300,150 340,130" fill="none" stroke={LIME} strokeWidth="3" />
    {/* Data points with pulse */}
    <circle cx="340" cy="70" r="5" fill={CYAN}>
      <animate attributeName="r" values="5;8;5" dur="1.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="340" cy="130" r="5" fill={LIME}>
      <animate attributeName="r" values="5;8;5" dur="1.5s" repeatCount="indefinite" begin="0.5s" />
    </circle>
    {/* Legend */}
    <rect x="60" y="70" width="10" height="10" fill={CYAN} />
    <text x="76" y="79" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK}>Scans</text>
    <rect x="130" y="70" width="10" height="10" fill={LIME} />
    <text x="146" y="79" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK}>Behoben</text>
    {/* KPIs */}
    <text x="60" y="105" fontFamily="ui-monospace, monospace" fontSize="9" fill={INK} letterSpacing="1">UPTIME</text>
    <text x="60" y="120" fontFamily="ui-monospace, monospace" fontSize="16" fontWeight="bold" fill={NAVY}>99.98%</text>
    <text x="200" y="245" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill={INK} letterSpacing="2">
      24/7 MONITORING · REAL-TIME
    </text>
  </svg>
);

// 6. AUDIT/COMPLIANCE – "Wir dokumentieren alles für Ihren Auditor"
const AuditIllu = () => (
  <svg viewBox="0 0 400 260" className="w-full h-auto" role="img" aria-label="Audit-fähige Compliance-Reports">
    <rect width="400" height="260" fill={LIGHT} />
    {/* Stack of documents */}
    <rect x="80" y="70" width="180" height="150" rx="4" fill="#fff" stroke="#D3DEE6" strokeWidth="1" transform="rotate(-4 170 145)" />
    <rect x="90" y="60" width="180" height="150" rx="4" fill="#fff" stroke="#D3DEE6" strokeWidth="1" transform="rotate(-2 180 135)" />
    {/* Main document */}
    <rect x="100" y="50" width="180" height="160" rx="4" fill="#fff" stroke={NAVY} strokeWidth="2" />
    {/* Document header */}
    <rect x="100" y="50" width="180" height="24" rx="4" fill={NAVY} />
    <text x="190" y="66" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="9" fill="#fff" letterSpacing="2">AUDIT-REPORT</text>
    {/* Content lines */}
    <rect x="115" y="90" width="120" height="4" fill={INK} />
    <rect x="115" y="102" width="150" height="3" fill="#D3DEE6" />
    <rect x="115" y="112" width="140" height="3" fill="#D3DEE6" />
    <rect x="115" y="122" width="145" height="3" fill="#D3DEE6" />
    {/* Checked items */}
    {[142, 158, 174, 190].map((y, i) => (
      <g key={y}>
        <circle cx="120" cy={y} r="5" fill={LIME} />
        <text x="120" y={y + 3} textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="6" fontWeight="bold" fill={INK}>✓</text>
        <rect x="135" y={y - 2} width={90 - i * 5} height="3" fill="#D3DEE6" />
      </g>
    ))}
    {/* Stamp */}
    <g transform="translate(300, 130) rotate(-8)">
      <circle cx="0" cy="0" r="34" fill="none" stroke={LIME} strokeWidth="3" />
      <text x="0" y="-4" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="8" fill={LIME} fontWeight="bold">NIS-2</text>
      <text x="0" y="8" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="7" fill={LIME} fontWeight="bold">CONFORM</text>
    </g>
    <text x="200" y="245" textAnchor="middle" fontFamily="ui-monospace, monospace" fontSize="10" fill={INK} letterSpacing="2">
      NIS-2 · DSGVO · ISO 27001
    </text>
  </svg>
);

const EXPLAINERS = [
  {
    step: "01",
    illu: InventoryIllu,
    title: "Wir erfassen erst mal alles",
    desc: "Bevor wir schützen können, dokumentieren wir jedes Gerät, jeden Server und jede Software in Ihrer IT. Ohne diese Landkarte ist Schutz nur Bauchgefühl.",
    testId: "inventory",
  },
  {
    step: "02",
    illu: ScanIllu,
    title: "Wir scannen 24/7 nach Lücken",
    desc: "Mit Greenbone durchsuchen wir kontinuierlich Ihre gesamte IT nach bekannten Sicherheitslücken – wie ein Radar, der Angriffsflächen sichtbar macht.",
    testId: "scan",
  },
  {
    step: "03",
    illu: CvssIllu,
    title: "Wir bewerten, wie kritisch es ist",
    desc: "Nicht jede Lücke ist gleich gefährlich. Nach dem internationalen CVSS-Standard bewerten wir jede Schwachstelle von 0 (harmlos) bis 10 (kritisch).",
    testId: "cvss",
  },
  {
    step: "04",
    illu: PatchIllu,
    title: "Wir schließen die Lücken",
    desc: "Kritische Lücken werden priorisiert und automatisiert gepatcht. Sie bekommen einen Bericht darüber, was wann repariert wurde – ohne selbst Hand anlegen zu müssen.",
    testId: "patch",
  },
  {
    step: "05",
    illu: MonitorIllu,
    title: "Wir bleiben rund um die Uhr wachsam",
    desc: "Neue Schwachstellen entstehen täglich. Unser SOC überwacht Ihre Systeme kontinuierlich – auch nachts, am Wochenende und im Urlaub.",
    testId: "monitor",
  },
  {
    step: "06",
    illu: AuditIllu,
    title: "Wir liefern Ihnen die Dokumentation",
    desc: "Für NIS-2, DSGVO und ISO 27001 brauchen Sie Nachweise. Wir liefern monatliche Reports und Audit-Dokumente – Ihr Prüfer wird zufrieden sein.",
    testId: "audit",
  },
];

export const IllustratedExplainers = () => {
  return (
    <section className="bg-white border-y border-line" data-testid="illustrated-explainers">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
        <div className="max-w-3xl">
          <Eyebrow>So funktioniert Schwachstellenmanagement</Eyebrow>
          <h2 className="mt-6 font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tightest leading-[1.02] text-ink">
            Sechs Schritte.<br />
            <span className="text-primary">Einfach erklärt.</span>
          </h2>
          <p className="mt-6 text-muted text-lg leading-relaxed max-w-2xl">
            Schwachstellenmanagement klingt technisch – ist es aber nicht. Hier zeigen wir Ihnen visuell, was wir für Sie tun. Ohne Fach-Chinesisch.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {EXPLAINERS.map((e) => (
            <article
              key={e.step}
              data-testid={`explainer-${e.testId}`}
              className="border border-line bg-white overflow-hidden lift"
            >
              <div className="bg-soft border-b border-line">
                <e.illu />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3">
                  <span className="font-display font-black text-3xl text-accent">{e.step}</span>
                  <h3 className="font-display font-bold text-xl text-ink leading-tight">{e.title}</h3>
                </div>
                <p className="mt-4 text-muted text-sm leading-relaxed">{e.desc}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
